import * as React from "react";
import {
  Typography,
  Box,
  Grid,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
  Switch,
  CircularProgress,
  Chip,
  alpha,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from "@mui/material";
import { CheckCircle, Cancel, Email, Schedule, Summarize, Receipt, Save } from "@mui/icons-material";
import { connect } from "react-redux";
import {
  MainInput,
  centered_flex_box,
  left_flex_box,
  main_button,
} from "../app/components/Styles";
import NewCard from "../app/components/NewCard";
import moment from "moment";
import { getNotificationSettings, updateNotificationSettings } from "../app/store/actions/dataActions";
import { useTranslation } from 'react-i18next';
import { Language } from '@mui/icons-material';

const DIGEST_TYPES = ["Orders", "Inbounds", "Backtrack"];

export const Profile = ({
  user,
  isLoading,
  notificationSettings,
  isWaiting,
  getNotificationSettings,
  updateNotificationSettings,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = React.useState(0);

  const handleLanguageChange = (event) => {
    const newLang = event.target.value;
    i18n.changeLanguage(newLang);
    localStorage.setItem('vendorLanguage', newLang);
  };

  const [settings, setSettings] = React.useState({
    sendDailyDigestNotifications: false,
    sendWeeklyDigestNotifications: false,
    dailyDigestNotificationsTypes: [],
    sendTransactionNotifications: false,
    sendNotificationsOnEmail: false,
  });
  const [settingsFetched, setSettingsFetched] = React.useState(false);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  React.useEffect(() => {
    if (activeTab === 3 && !settingsFetched) {
      getNotificationSettings();
      setSettingsFetched(true);
    }
  }, [activeTab, settingsFetched, getNotificationSettings]);

  const hasReturnSystem = Boolean(user?.vendor?.hasReturnSystem);
  const availableDigestTypes = React.useMemo(
    () => (hasReturnSystem ? DIGEST_TYPES : DIGEST_TYPES.filter((t) => t !== "Backtrack")),
    [hasReturnSystem]
  );

  React.useEffect(() => {
    if (notificationSettings) {
      let types = Array.isArray(notificationSettings.dailyDigestNotificationsTypes)
        ? notificationSettings.dailyDigestNotificationsTypes
        : [];
      if (!hasReturnSystem) {
        types = types.filter((t) => t !== "Backtrack");
      }
      setSettings({
        sendDailyDigestNotifications: notificationSettings.sendDailyDigestNotifications ?? false,
        sendWeeklyDigestNotifications: notificationSettings.sendWeeklyDigestNotifications ?? false,
        dailyDigestNotificationsTypes: types,
        sendTransactionNotifications: notificationSettings.sendTransactionNotifications ?? false,
        sendNotificationsOnEmail: notificationSettings.sendNotificationsOnEmail ?? false,
      });
    }
  }, [notificationSettings, hasReturnSystem]);

  const handleSettingChange = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleDigestTypeToggle = (type) => {
    if (type === "Backtrack" && !hasReturnSystem) return;
    setSettings((prev) => {
      const types = prev.dailyDigestNotificationsTypes || [];
      const newTypes = types.includes(type)
        ? types.filter((t) => t !== type)
        : [...types, type];
      return { ...prev, dailyDigestNotificationsTypes: newTypes };
    });
  };

  const handleSaveNotificationSettings = () => {
    const payload = { ...settings };
    if (!hasReturnSystem && Array.isArray(payload.dailyDigestNotificationsTypes)) {
      payload.dailyDigestNotificationsTypes = payload.dailyDigestNotificationsTypes.filter((t) => t !== "Backtrack");
    }
    updateNotificationSettings(payload);
  };

  // Tab index mapping
  const tabIndexMap = {
    'user-info': 0,
    'vendor-info': 1,
    'subscriptions': 2,
    'notification-settings': 3,
  };

  const tabValueMap = ['user-info', 'vendor-info', 'subscriptions', 'notification-settings'];

  return (
    <div>
      <Box sx={{ width: "100%", paddingX: isMobile ? 2 : 5 }}>
        {/* Profile Header Section */}
        <Typography variant="h4" sx={{ color: "var(--secColor4)", fontWeight: "bold", mb: 3, mt: 2 }}>
          {t('profile.title')}
        </Typography>

        {/* Tabs Section */}
        <Box sx={{
          borderBottom: 1,
          borderColor: 'divider',
          mb: 3,
          '& .MuiTabs-root': {
            minHeight: '48px',
          },
          '& .MuiTab-root': {
            color: 'var(--secColor4)',
            textTransform: 'none',
            fontWeight: 500,
            fontSize: isMobile ? '0.9rem' : '1rem',
            minHeight: '48px',
            '&.Mui-selected': {
              color: 'var(--primaryColor)',
              fontWeight: 600,
            },
            '&:hover': {
              color: 'var(--primaryColor)',
              opacity: 0.8,
            },
          },
          '& .MuiTabs-indicator': {
            backgroundColor: 'var(--primaryColor)',
            height: '3px',
          },
        }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant={isMobile ? "scrollable" : "standard"}
            scrollButtons={isMobile ? "auto" : false}
            aria-label="profile tabs"
          >
            <Tab label={t('profile.userInfo') || 'User Info'} />
            <Tab label={t('profile.brandInfo') || 'Brand Info'} />
            <Tab label={t('profile.subscriptions') || 'Subscriptions'} />
            <Tab label={t('profile.notificationSettings')} />
            <Tab label="ConfirmIt Settings" />
          </Tabs>
        </Box>

        {/* Tab Content */}
        <Box sx={{ mt: 3 }}>
          {/* User Info Tab */}
          {activeTab === 0 && (
            <NewCard content={
              <Box sx={{ px: isMobile ? 2 : 5, py: 3 }}>
                <Typography variant="h6" fontWeight={"bold"} marginBottom={3} sx={{ color: "var(--secColor4)" }}>
                  {t('profile.personalInfo')}
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "var(--textSecondary)", mb: 1 }}>
                      {t('common.name')}
                    </Typography>
                    <MainInput
                      margin="normal"
                      fullWidth
                      value={user?.name || ''}
                      disabled
                      sx={{ backgroundColor: "var(--appBg)" }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "var(--textSecondary)", mb: 1 }}>
                      {t('common.email')}
                    </Typography>
                    <MainInput
                      margin="normal"
                      fullWidth
                      value={user?.email || ''}
                      disabled
                      sx={{ backgroundColor: "var(--appBg)" }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "var(--textSecondary)", mb: 1 }}>
                      {t('profile.preferredLanguage')}
                    </Typography>
                    <FormControl fullWidth sx={{ mt: 1 }}>
                      <Select
                        value={i18n.language}
                        onChange={handleLanguageChange}
                        sx={{
                          backgroundColor: "var(--appBg)",
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: "var(--borderColor)",
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: "var(--primaryColor)",
                          },
                          '& .MuiSelect-select': {
                            color: "var(--secColor4)",
                          },
                        }}
                      >
                        <MenuItem value="en">{t('language.english')}</MenuItem>
                        <MenuItem value="ar">{t('language.arabic')}</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
            } />
          )}

          {/* Brand/Vendor Info Tab */}
          {activeTab === 1 && (
            <NewCard content={
              <Box sx={{ px: isMobile ? 2 : 5, py: 3 }}>
                <Typography variant="h6" fontWeight={"bold"} marginBottom={3} sx={{ color: "var(--secColor4)" }}>
                  {t('profile.vendorInfo')}
                </Typography>
                {user?.vendor ? (
                  <>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ color: "var(--textSecondary)", mb: 1 }}>
                        {t('profile.brandName') || 'Brand Name'}
                      </Typography>
                      <MainInput
                        margin="normal"
                        fullWidth
                        value={user.vendor.brandName || ''}
                        disabled
                        sx={{ backgroundColor: "var(--appBg)" }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ color: "var(--textSecondary)", mb: 1 }}>
                        {t('common.email')}
                      </Typography>
                      <MainInput
                        margin="normal"
                        fullWidth
                        value={user.vendor.email || ''}
                        disabled
                        sx={{ backgroundColor: "var(--appBg)" }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ color: "var(--textSecondary)", mb: 1 }}>
                        {t('profile.primaryColor') || 'Primary Color'}
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Box
                          sx={{
                            width: 60,
                            height: 60,
                            backgroundColor: user.vendor.primaryColor || '#FFFFFF',
                            border: '2px solid var(--secColor4)',
                            borderRadius: 2,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          }}
                        />
                        <Typography variant="body1" sx={{ color: "var(--secColor4)", fontWeight: 500 }}>
                          {user.vendor.primaryColor || t('profile.notSet') || 'Not Set'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ color: "var(--textSecondary)", mb: 1 }}>
                        {t('profile.secondaryColor') || 'Secondary Color'}
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Box
                          sx={{
                            width: 60,
                            height: 60,
                            backgroundColor: user.vendor.secondaryColor || '#000000',
                            border: '2px solid var(--secColor4)',
                            borderRadius: 2,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          }}
                        />
                        <Typography variant="body1" sx={{ color: "var(--secColor4)", fontWeight: 500 }}>
                          {user.vendor.secondaryColor || t('profile.notSet') || 'Not Set'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ color: "var(--textSecondary)", mb: 1 }}>
                        {t('profile.shippingService') || 'Shipping Service'}
                      </Typography>
                      <MainInput
                        margin="normal"
                        fullWidth
                        value={user.vendor.shippingService || ''}
                        disabled
                        sx={{ backgroundColor: "var(--appBg)" }}
                      />
                    </Grid>
                    {user?.vendor?.hasReturnSystem && (
                      <>
                        <Grid item xs={12}>
                          <Typography variant="body2" sx={{ color: "var(--textSecondary)", mb: 1 }}>
                            {t('profile.returnPolicy')}
                          </Typography>
                          <MainInput
                            margin="normal"
                            fullWidth
                            multiline
                            rows={4}
                            value={user.vendor.returnPolicy || ''}
                            disabled
                            sx={{ backgroundColor: "var(--appBg)" }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" sx={{ color: "var(--textSecondary)", mb: 1 }}>
                            {t('profile.returnPolicyDays')}
                          </Typography>
                          <MainInput
                            margin="normal"
                            fullWidth
                            value={user.vendor.returnPolicyDays || ''}
                            disabled
                            sx={{ backgroundColor: "var(--appBg)" }}
                          />
                        </Grid>
                      </>
                    )}
                  </Grid>

                  <Box sx={{ mt: 4 }}>
                    <Typography variant="h6" fontWeight={"bold"} marginBottom={2} sx={{ color: "var(--secColor4)" }}>
                      {t('profile.settings') || 'Settings'}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ 
                          display: "flex", 
                          alignItems: "center", 
                          gap: 2,
                          p: 2,
                          borderRadius: 2,
                          backgroundColor: user.vendor.allowToOpenPackage ? "rgba(76, 175, 80, 0.1)" : "rgba(158, 158, 158, 0.1)",
                          border: `1px solid ${user.vendor.allowToOpenPackage ? "rgba(76, 175, 80, 0.3)" : "rgba(158, 158, 158, 0.3)"}`
                        }}>
                          {user.vendor.allowToOpenPackage ? (
                            <CheckCircle sx={{ color: "#4caf50", fontSize: 24 }} />
                          ) : (
                            <Cancel sx={{ color: "#9e9e9e", fontSize: 24 }} />
                          )}
                          <Typography variant="body1" sx={{ color: "var(--secColor4)", fontWeight: 500 }}>
                            {t('profile.allowToOpenPackage') || 'Allow To Open Package'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ 
                          display: "flex", 
                          alignItems: "center", 
                          gap: 2,
                          p: 2,
                          borderRadius: 2,
                          backgroundColor: user.vendor.fragileOrders ? "rgba(76, 175, 80, 0.1)" : "rgba(158, 158, 158, 0.1)",
                          border: `1px solid ${user.vendor.fragileOrders ? "rgba(76, 175, 80, 0.3)" : "rgba(158, 158, 158, 0.3)"}`
                        }}>
                          {user.vendor.fragileOrders ? (
                            <CheckCircle sx={{ color: "#4caf50", fontSize: 24 }} />
                          ) : (
                            <Cancel sx={{ color: "#9e9e9e", fontSize: 24 }} />
                          )}
                          <Typography variant="body1" sx={{ color: "var(--secColor4)", fontWeight: 500 }}>
                            {t('profile.fragileOrders') || 'Fragile Orders'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ 
                          display: "flex", 
                          alignItems: "center", 
                          gap: 2,
                          p: 2,
                          borderRadius: 2,
                          backgroundColor: user.vendor.receiveOrders ? "rgba(76, 175, 80, 0.1)" : "rgba(158, 158, 158, 0.1)",
                          border: `1px solid ${user.vendor.receiveOrders ? "rgba(76, 175, 80, 0.3)" : "rgba(158, 158, 158, 0.3)"}`
                        }}>
                          {user.vendor.receiveOrders ? (
                            <CheckCircle sx={{ color: "#4caf50", fontSize: 24 }} />
                          ) : (
                            <Cancel sx={{ color: "#9e9e9e", fontSize: 24 }} />
                          )}
                          <Typography variant="body1" sx={{ color: "var(--secColor4)", fontWeight: 500 }}>
                            {t('profile.receiveOrders') || 'Receive Orders'}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                  </>
                ) : (
                  <Typography sx={{ color: "var(--textSecondary)" }}>
                    {t('profile.noVendorInfo') || 'No vendor information available.'}
                  </Typography>
                )}
              </Box>
            } />
          )}

          {/* Subscriptions Tab */}
          {activeTab === 2 && (
            <NewCard content={
              <Box sx={{ px: isMobile ? 2 : 5, py: 3 }}>
                <Typography variant="h6" fontWeight={"bold"} marginBottom={3} sx={{ color: "var(--secColor4)" }}>
                  {t('profile.subscriptions') || 'Subscriptions'}
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Box sx={{ 
                      display: "flex", 
                      alignItems: "center", 
                      gap: 2,
                      p: 2,
                      borderRadius: 2,
                      backgroundColor: user?.vendor?.hasReturnSystem ? "rgba(76, 175, 80, 0.1)" : "rgba(158, 158, 158, 0.1)",
                      border: `1px solid ${user?.vendor?.hasReturnSystem ? "rgba(76, 175, 80, 0.3)" : "rgba(158, 158, 158, 0.3)"}`
                    }}>
                      {user?.vendor?.hasReturnSystem ? (
                        <>
                          <CheckCircle sx={{ color: "#4caf50", fontSize: 32 }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" sx={{ color: "var(--secColor4)", fontWeight: 500, mb: 0.5 }}>
                              Backtrack {t('profile.active') || 'Active'}
                            </Typography>
                            {user?.vendor?.returnSystemSubscriptionDate && (
                              <Typography variant="body2" sx={{ color: "var(--textSecondary)" }}>
                                {t('profile.subscribedOn') || 'Subscribed on'} {moment(user.vendor.returnSystemSubscriptionDate).format("MMMM D, YYYY")}
                              </Typography>
                            )}
                          </Box>
                        </>
                      ) : (
                        <>
                          <Cancel sx={{ color: "#9e9e9e", fontSize: 32 }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" sx={{ color: "var(--secColor4)", fontWeight: 500 }}>
                              Backtrack {t('profile.inactive') || 'Inactive'}
                            </Typography>
                          </Box>
                        </>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ 
                      display: "flex", 
                      alignItems: "center", 
                      gap: 2,
                      p: 2,
                      borderRadius: 2,
                      backgroundColor: user?.vendor?.hasPulseUnlimited ? "rgba(76, 175, 80, 0.1)" : "rgba(158, 158, 158, 0.1)",
                      border: `1px solid ${user?.vendor?.hasPulseUnlimited ? "rgba(76, 175, 80, 0.3)" : "rgba(158, 158, 158, 0.3)"}`
                    }}>
                      {user?.vendor?.hasPulseUnlimited ? (
                        <>
                          <CheckCircle sx={{ color: "#4caf50", fontSize: 32 }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" sx={{ color: "var(--secColor4)", fontWeight: 500, mb: 0.5 }}>
                              Chainz Pulse {t('profile.active') || 'Active'}
                            </Typography>
                            {user?.vendor?.pulseSubscriptionStartDate && (
                              <Typography variant="body2" sx={{ color: "var(--textSecondary)" }}>
                                {t('profile.subscribedOn') || 'Subscribed on'} {moment(user.vendor.pulseSubscriptionStartDate).format("MMMM D, YYYY")}
                              </Typography>
                            )}
                          </Box>
                        </>
                      ) : (
                        <>
                          <Cancel sx={{ color: "#9e9e9e", fontSize: 32 }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" sx={{ color: "var(--secColor4)", fontWeight: 500 }}>
                              Chainz Pulse {t('profile.inactive') || 'Inactive'}
                            </Typography>
                          </Box>
                        </>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ 
                      display: "flex", 
                      alignItems: "center", 
                      gap: 2,
                      p: 2,
                      borderRadius: 2,
                      backgroundColor: user?.shopifyDomain ? "rgba(76, 175, 80, 0.1)" : "rgba(158, 158, 158, 0.1)",
                      border: `1px solid ${user?.shopifyDomain ? "rgba(76, 175, 80, 0.3)" : "rgba(158, 158, 158, 0.3)"}`
                    }}>
                      {user?.shopifyDomain ? (
                        <>
                          <CheckCircle sx={{ color: "#4caf50", fontSize: 32 }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" sx={{ color: "var(--secColor4)", fontWeight: 500, mb: 0.5 }}>
                              {t('profile.shopifyConnected') || 'Shopify Connected'}
                            </Typography>
                            <Typography variant="body2" sx={{ color: "var(--textSecondary)" }}>
                              {user.shopifyDomain}
                            </Typography>
                          </Box>
                        </>
                      ) : (
                        <>
                          <Cancel sx={{ color: "#9e9e9e", fontSize: 32 }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" sx={{ color: "var(--secColor4)", fontWeight: 500 }}>
                              {t('profile.shopifyNotConnected') || 'Shopify Not Connected'}
                            </Typography>
                          </Box>
                        </>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            } />
          )}

          {/* Notification Settings Tab */}
          {activeTab === 3 && (
            <NewCard content={
              <Box sx={{ px: isMobile ? 2 : 5, py: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                  <Email sx={{ color: "var(--primaryColor)", fontSize: 28 }} />
                  <Typography variant="h6" fontWeight={600} sx={{ color: "var(--secColor4)" }}>
                    {t('profile.notificationSettings')}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
                  <Chip
                    icon={<Schedule sx={{ fontSize: 16 }} />}
                    label={t('profile.emailsAt6pm') || 'Emails at 6pm'}
                    size="small"
                    sx={{
                      height: 24,
                      fontSize: "0.75rem",
                      bgcolor: alpha("#F7463C", 0.12),
                      color: "var(--primaryColor)",
                      border: "none",
                      "& .MuiChip-icon": { color: "inherit" },
                    }}
                  />
                </Box>

                {isLoading && !notificationSettings ? (
                  <Box sx={{ ...centered_flex_box, py: 6 }}>
                    <CircularProgress sx={{ color: "var(--primaryColor)" }} />
                  </Box>
                ) : (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {/* Daily Digest */}
                    <Box
                      sx={{
                        p: 2.5,
                        borderRadius: 2,
                        border: "1px solid var(--borderColor)",
                        bgcolor: "var(--appBg)",
                        transition: "all 0.2s ease",
                        "&:hover": { borderColor: "rgba(247, 70, 60, 0.4)" },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
                        <Box sx={{ display: "flex", gap: 2, flex: 1, minWidth: 0 }}>
                          <Summarize sx={{ color: "var(--primaryColor)", mt: 0.25, flexShrink: 0 }} />
                          <Box>
                            <Typography variant="subtitle1" fontWeight={600} sx={{ color: "var(--secColor4)" }}>
                              {t('profile.dailyDigest') || 'Daily Digest'}
                            </Typography>
                            <Typography variant="body2" sx={{ color: "var(--textSecondary)", mt: 0.5 }}>
                              {t('profile.dailyDigestDescription') || 'Every work day (Sat–Thu). Summary of the day in your chosen categories.'}
                            </Typography>
                          </Box>
                        </Box>
                        <Switch
                          checked={settings.sendDailyDigestNotifications}
                          onChange={(e) => handleSettingChange("sendDailyDigestNotifications", e.target.checked)}
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": { color: "var(--primaryColor)" },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "var(--primaryColor) !important" },
                          }}
                        />
                      </Box>
                    </Box>

                    {/* Weekly Digest */}
                    <Box
                      sx={{
                        p: 2.5,
                        borderRadius: 2,
                        border: "1px solid var(--borderColor)",
                        bgcolor: "var(--appBg)",
                        transition: "all 0.2s ease",
                        "&:hover": { borderColor: "rgba(247, 70, 60, 0.4)" },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                        <Box sx={{ display: "flex", gap: 2, flex: 1, minWidth: 0 }}>
                          <Schedule sx={{ color: "var(--primaryColor)", flexShrink: 0 }} />
                          <Box>
                            <Typography variant="subtitle1" fontWeight={600} sx={{ color: "var(--secColor4)" }}>
                              {t('profile.weeklyDigest') || 'Weekly Digest'}
                            </Typography>
                            <Typography variant="body2" sx={{ color: "var(--textSecondary)", mt: 0.5 }}>
                              {t('profile.weeklyDigestDescription') || 'Every Thursday. Full week summary in your chosen categories.'}
                            </Typography>
                          </Box>
                        </Box>
                        <Switch
                          checked={settings.sendWeeklyDigestNotifications}
                          onChange={(e) => handleSettingChange("sendWeeklyDigestNotifications", e.target.checked)}
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": { color: "var(--primaryColor)" },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "var(--primaryColor) !important" },
                          }}
                        />
                      </Box>
                    </Box>

                    {/* Digest categories - shared by daily & weekly */}
                    {(settings.sendDailyDigestNotifications || settings.sendWeeklyDigestNotifications) && (
                      <Box
                        sx={{
                          p: 2.5,
                          borderRadius: 2,
                          border: "1px solid var(--borderColor)",
                          bgcolor: "var(--appBg)",
                          "&:hover": { borderColor: "rgba(247, 70, 60, 0.4)" },
                        }}
                      >
                        <Typography variant="subtitle2" fontWeight={600} sx={{ color: "var(--secColor4)", mb: 2 }}>
                          {t('profile.categoriesToInclude') || 'Categories to include in digests'}
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                          {availableDigestTypes.map((type) => {
                            const selected = (settings.dailyDigestNotificationsTypes || []).includes(type);
                            const typeLabel = type === "Backtrack" ? "Backtrack" : t(`profile.${type.toLowerCase()}`) || type;
                            return (
                              <Chip
                                key={type}
                                label={typeLabel}
                                size="small"
                                onClick={() => handleDigestTypeToggle(type)}
                                sx={{
                                  cursor: "pointer",
                                  fontWeight: 500,
                                  bgcolor: selected ? "var(--primaryColor)" : "transparent",
                                  color: selected ? "white" : "var(--secColor4)",
                                  border: `1px solid ${selected ? "var(--primaryColor)" : "var(--borderColor)"}`,
                                  "&:hover": {
                                    bgcolor: selected ? "var(--primaryColor)" : alpha("#F7463C", 0.08),
                                    borderColor: "var(--primaryColor)",
                                  },
                                }}
                              />
                            );
                          })}
                        </Box>
                      </Box>
                    )}

                    {/* Transaction & Email toggles - compact row */}
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                        gap: 2,
                      }}
                    >
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          border: "1px solid var(--borderColor)",
                          bgcolor: "var(--appBg)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 2,
                          "&:hover": { borderColor: "rgba(247, 70, 60, 0.4)" },
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Receipt sx={{ color: "var(--primaryColor)", fontSize: 22 }} />
                          <Typography variant="subtitle2" fontWeight={600} sx={{ color: "var(--secColor4)" }}>
                            {t('profile.sendTransactionNotifications')}
                          </Typography>
                        </Box>
                        <Switch
                          checked={settings.sendTransactionNotifications}
                          onChange={(e) => handleSettingChange("sendTransactionNotifications", e.target.checked)}
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": { color: "var(--primaryColor)" },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "var(--primaryColor) !important" },
                          }}
                        />
                      </Box>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          border: "1px solid var(--borderColor)",
                          bgcolor: "var(--appBg)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 2,
                          "&:hover": { borderColor: "rgba(247, 70, 60, 0.4)" },
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Email sx={{ color: "var(--primaryColor)", fontSize: 22 }} />
                          <Typography variant="subtitle2" fontWeight={600} sx={{ color: "var(--secColor4)" }}>
                            {t('profile.sendNotificationsOnEmail')}
                          </Typography>
                        </Box>
                        <Switch
                          checked={settings.sendNotificationsOnEmail}
                          onChange={(e) => handleSettingChange("sendNotificationsOnEmail", e.target.checked)}
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": { color: "var(--primaryColor)" },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "var(--primaryColor) !important" },
                          }}
                        />
                      </Box>
                    </Box>

                    <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid var(--borderColor)" }}>
                      <Box
                        component="button"
                        onClick={handleSaveNotificationSettings}
                        disabled={isWaiting}
                        sx={{
                          ...main_button,
                          px: 3,
                          py: 1.5,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 1.5,
                          fontSize: "0.9375rem",
                        }}
                      >
                        {isWaiting ? (
                          <CircularProgress size={20} sx={{ color: "inherit" }} />
                        ) : (
                          <Save sx={{ fontSize: 20 }} />
                        )}
                        {t('profile.saveSettings')}
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            } />
          )}

          {/* ConfirmIt Settings Tab */}
          {activeTab === 4 && (
            <NewCard content={
              <Box sx={{ px: isMobile ? 2 : 5, py: 3 }}>
                <Typography variant="h6" fontWeight={"bold"} marginBottom={3} sx={{ color: "var(--secColor4)" }}>
                  ConfirmIt Settings
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "var(--textSecondary)", mb: 1 }}>
                      Uses ConfirmIt
                    </Typography>
                    <MainInput
                      margin="normal"
                      fullWidth
                      value={String(Boolean(user?.vendor?.usesConfirmIt))}
                      disabled
                      sx={{ backgroundColor: "var(--appBg)" }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "var(--textSecondary)", mb: 1 }}>
                      Sends Orders To ConfirmIt
                    </Typography>
                    <MainInput
                      margin="normal"
                      fullWidth
                      value={String(Boolean(user?.vendor?.sendsOrdersToConfirmIt))}
                      disabled
                      sx={{ backgroundColor: "var(--appBg)" }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" sx={{ color: "var(--textSecondary)", mb: 1 }}>
                      ConfirmIt UUID
                    </Typography>
                    <MainInput
                      margin="normal"
                      fullWidth
                      value={user?.vendor?.confirmItUUID || ""}
                      disabled
                      sx={{ backgroundColor: "var(--appBg)" }}
                    />
                  </Grid>
                </Grid>
              </Box>
            } />
          )}
        </Box>
      </Box>
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state?.auth?.user,
  isLoading: state?.records?.isLoading,
  notificationSettings: state?.records?.notificationSettings,
  isWaiting: state?.wait?.isWaiting,
});

const mapDispatchToProps = {
  getNotificationSettings,
  updateNotificationSettings,
};

export default connect(mapStateToProps, mapDispatchToProps)(Profile);
